import { useCallback } from 'react';
import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import {
	Behaviours,
	Prompts,
	TargetTabs,
	createProvisioningApiExtensions,
} from '@atlassiansox/cross-flow-extensions';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { useFirePostOfficeTrigger } from '@atlassian/post-office-context';
import type { RouteName } from '@atlassian/jira-route-name-types/src/RouteName.tsx';
import { JPD_ROADMAP_ON_TIMELINE_SPOTLIGHT_TRIGGER } from './constants.tsx';
import type { InwardIssue } from './types.tsx';

export const useHasJpd = (): boolean => {
	const licensedProducts = useLicensedProducts();
	return licensedProducts['jira-product-discovery'] === true;
};

export const useIsTimelineRoute = (): boolean => {
	const timelineRoutes = new Set<RouteName>([
		'classic-business-timeline', // JWM
		'software-timeline', // TMP
		'software-timeline-classic', // CMP
	]);
	return useIsActiveRoute(timelineRoutes);
};

export const useFireJpdRoadmapOnTimelineSpotlightTrigger = () => {
	const isTimelineRoute = useIsTimelineRoute();
	const { fireTrigger } = useFirePostOfficeTrigger({
		triggerId: JPD_ROADMAP_ON_TIMELINE_SPOTLIGHT_TRIGGER,
	});

	const triggerSpotlight = useCallback(() => {
		if (isTimelineRoute) {
			fireTrigger({ isActive: true });
		}
	}, [fireTrigger, isTimelineRoute]);

	return triggerSpotlight;
};

export const useResetJpdRoadmapOnTimelineSpotlightTrigger = () => {
	const { fireTrigger } = useFirePostOfficeTrigger({
		triggerId: JPD_ROADMAP_ON_TIMELINE_SPOTLIGHT_TRIGGER,
	});

	const triggerSpotlight = useCallback(() => {
		fireTrigger({ isActive: false });
	}, [fireTrigger]);

	return triggerSpotlight;
};

const sortIssuesEpicFirst = (a: InwardIssue, b: InwardIssue) => {
	if (a.issueType === 'epic' && b.issueType === 'task') {
		return -1;
	}
	if (a.issueType === 'task' && b.issueType === 'epic') {
		return 1;
	}
	return 0;
};

export const STATUS_CATEGORY_IN_PROGRESS = '4';
export const STATUS_CATEGORY_TODO = '2';

const sortIssuesInProgressFirst = (a: InwardIssue, b: InwardIssue) => {
	if (
		a.status.categoryId === STATUS_CATEGORY_IN_PROGRESS &&
		b.status.categoryId === STATUS_CATEGORY_TODO
	) {
		return -1;
	}
	if (
		a.status.categoryId === STATUS_CATEGORY_TODO &&
		b.status.categoryId === STATUS_CATEGORY_IN_PROGRESS
	) {
		return 1;
	}
	return 0;
};

const sortIssues = (a: InwardIssue, b: InwardIssue) => {
	let result = sortIssuesEpicFirst(a, b);
	if (result === 0) {
		result = sortIssuesInProgressFirst(a, b);
	}
	if (result === 0) {
		result = b.id.localeCompare(a.id);
	}
	return result;
};

export const pickIssuesToConvert = (issues: InwardIssue[], maxCount = 5) => {
	return issues.sort(sortIssues).slice(0, maxCount);
};

export const useOpenCrossFlow = (inwardIssues: InwardIssue[]) => {
	const crossFlow = useCrossFlow();
	const templateId = 'jpd.product-roadmap';
	const inwardIssueIds = inwardIssues.map(({ id }) => id);
	const extensions = createProvisioningApiExtensions({
		actions: {
			primary: {
				behaviour: Behaviours.LAND,
				prompt: Prompts.NEXT,
				options: {
					targetTab: TargetTabs.SAME,
					urlParams: { templateId, inwardIssueIds: inwardIssueIds.join(',') },
				},
			},
		},
		jobName: 'jira-timeline-jpd-roadmap',
		jobParams: {
			inwardIssueIds,
		},
	});

	return useCallback(() => {
		crossFlow.isEnabled &&
			crossFlow.apiV1.open({
				journey: Journeys.GET_STARTED,
				targetProduct: Targets.JIRA_PRODUCT_DISCOVERY,
				sourceComponent: 'recJpdOnJiraTimelineMoreSettings',
				sourceContext: 'jpdRoadmapButton',
				extensions,
				v1: true,
			});
	}, [crossFlow, extensions]);
};
